// Read-once query-parameter prefill for calculators opened from another tool (e.g. the
// Tyre Size Calculator's "Use the new tyre in" buttons). Each param is validated with the
// target tool's own field spec + validator; valid values override that tool's defaults,
// invalid ones are ignored and reported in a toast. Params are read once on mount only --
// editing a field afterwards never re-applies them.

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

export function readQueryPrefill(search, specs, validate) {
  const params = new URLSearchParams(search);
  const values = {};
  const rejected = [];
  specs.forEach((spec) => {
    if (!params.has(spec.key)) return;
    const raw = params.get(spec.key).trim();
    const error = validate(spec, raw);
    if (error) rejected.push({ key: spec.key, label: spec.label, value: raw, error });
    else values[spec.key] = raw;
  });
  return { values, rejected };
}

// Returns { values, rejected } from the current URL, captured on first render, and shows one
// toast describing what was applied / ignored. The ref guard keeps React StrictMode's
// double-invoked mount effect from toasting twice.
export function useQueryPrefill(specs, validate) {
  const location = useLocation();
  const [prefill] = useState(() => readQueryPrefill(location.search, specs, validate));
  const toastedRef = useRef(false);

  useEffect(() => {
    // Deferred: on a full page load this effect runs before App's <Toaster> has subscribed,
    // and sonner drops toasts fired before that. The guard sits inside the timer so
    // StrictMode's mount -> cleanup -> mount still toasts exactly once.
    const timer = setTimeout(() => {
      if (toastedRef.current) return;
      toastedRef.current = true;
      const applied = Object.keys(prefill.values);
      if (applied.length) {
        const labels = specs.filter((s) => applied.includes(s.key)).map((s) => `${s.label} ${prefill.values[s.key]}${s.unit ? ` ${s.unit}` : ''}`);
        toast.success(`Prefilled from link: ${labels.join(', ')}.`);
      }
      prefill.rejected.forEach((r) => {
        toast.error(`Ignored ${r.label} "${r.value}" from the link: ${r.error}`);
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [prefill, specs]);

  return prefill;
}
